import React, { createContext, useContext } from 'react';

import { UserSession } from 'stores/AuthenticationStoreAdapter';
import { ListFinancialYearResponse } from 'stores/FinancialYearStoreAdapter';
import { ManagerResult } from 'stores/ProjectStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';

export type AuthContextType = {
  userSession: UserSession;
  users: UserResult[];
  managers: ManagerResult[];
  backOfficeUsers: UserResult[];
  financialYearData: ListFinancialYearResponse;
};

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * 認証コンテキストからデータを取得するフック。
 * AuthenticatedRoute 配下のコンポーネントでのみ使用可能。
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  value: AuthContextType;
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ value, children }) => {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * オプショナル認証コンテキスト。
 * DevInsights（記事）のようにゲスト（未ログイン）でも閲覧できる画面向け。
 * userSession はログイン済みなら値を持ち、ゲスト時は null。
 */
export type OptionalAuthContextType = {
  userSession: UserSession | null;
  /** ユーザー一覧（著者選択などに使用。ゲスト時は空配列） */
  users: UserResult[];
};

const OptionalAuthContext = createContext<OptionalAuthContextType | null>(null);

/**
 * オプショナル認証コンテキストからデータを取得するフック。
 * OptionalAuthLayoutRoute 配下でのみ使用可能。ゲスト時は userSession が null。
 */
export const useOptionalAuth = (): OptionalAuthContextType => {
  const context = useContext(OptionalAuthContext);
  if (!context) {
    throw new Error('useOptionalAuth must be used within OptionalAuthProvider');
  }
  return context;
};

type OptionalAuthProviderProps = {
  value: OptionalAuthContextType;
  children: React.ReactNode;
};

export const OptionalAuthProvider: React.FC<OptionalAuthProviderProps> = ({ value, children }) => {
  return <OptionalAuthContext.Provider value={value}>{children}</OptionalAuthContext.Provider>;
};

/**
 * ユーザー一覧を取得する非スロー版フック。
 * AuthProvider（認証必須画面）と OptionalAuthProvider（ゲスト閲覧可の記事画面）の
 * どちらの配下でも動作し、どちらのコンテキストも無い場合は空配列を返す（例外を投げない）。
 *
 * knz のビューワー系コンポーネント（UserMentionInline / KnzArticleHeader など）は、
 * 認証必須のワークスペースと未認証可の記事詳細の両方で描画されるため、このフックを使う。
 */
export const useUsers = (): UserResult[] => {
  const auth = useContext(AuthContext);
  const optional = useContext(OptionalAuthContext);
  return auth?.users ?? optional?.users ?? [];
};
